import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, Users, Camera, CalendarClock, MoreHorizontal, Layers, Send, FileText, CalendarDays, Building2, Settings, LogOut, CloudOff, Cloud, Loader2 } from "lucide-react";
import { useAuth } from "@/context/Auth";
import { getQueue, onQueueChange, syncQueue } from "@/lib/offline";

const MAIN = [
  ["/", "Home", Home],
  ["/contacts", "Contacts", Users],
  ["/capture", "Capture", Camera],
  ["/followups", "Follow-ups", CalendarClock],
];
const SECONDARY = [
  ["/batch", "Batch upload", Layers],
  ["/campaigns", "Campaigns", Send],
  ["/templates", "Templates", FileText],
  ["/events", "Events", CalendarDays],
  ["/senders", "Sender businesses", Building2],
  ["/settings", "Settings & integrations", Settings],
];
export const MORE_LINKS = SECONDARY;

export function SyncBadge() {
  const [q, setQ] = useState([]);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    getQueue().then(setQ);
    syncQueue();
    const off = onQueueChange((queue, s) => { setQ(queue); setBusy(s); });
    const on = () => setOnline(true), offl = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", offl);
    return () => { off(); window.removeEventListener("online", on); window.removeEventListener("offline", offl); };
  }, []);
  if (!q.length && online) {
    return <span data-testid="sync-status-synced" className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700"><Cloud className="h-4 w-4" />Synced</span>;
  }
  return (
    <button data-testid="sync-status-pending" onClick={() => syncQueue()} className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700"
      title="Local captures are not backed up until synced">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudOff className="h-3.5 w-3.5" />}
      {!online ? "Offline · " : ""}{q.length ? `${q.length} saved on this device, waiting to upload` : "Captures will be kept on this device"}
    </button>
  );
}

function SideLink({ to, label, Icon }) {
  return (
    <NavLink to={to} end={to === "/"} data-testid={`sidebar-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-teal-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
      <Icon className="h-[18px] w-[18px]" />{label}
    </NavLink>
  );
}

export const SiteFooter = ({ className = "" }) => (
  <footer data-testid="site-footer" className={`mx-auto mt-12 max-w-6xl border-t border-slate-200 pt-4 text-center text-xs text-slate-500 ${className}`}>
    Site developed by:{" "}
    <a href="https://OpenBusinessAnywhere.com" target="_blank" rel="noopener noreferrer" data-testid="footer-developer-link" className="font-semibold text-teal-700 hover:underline">OpenBusinessAnywhere.com</a>
  </footer>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen">
      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col bg-slate-900 p-4 text-white md:flex">
        <div className="mb-8 rounded-xl bg-white px-3 py-2">
          <img src="/logo.png" alt="RingUs" data-testid="sidebar-logo" className="h-9 w-auto" />
        </div>
        <nav className="flex flex-col gap-1">{MAIN.map(([to, l, I]) => <SideLink key={to} to={to} label={l} Icon={I} />)}</nav>
        <div className="my-4 h-px bg-slate-800" />
        <nav className="flex flex-col gap-1">{SECONDARY.map(([to, l, I]) => <SideLink key={to} to={to} label={l} Icon={I} />)}</nav>
        <div className="mt-auto border-t border-slate-800 pt-4">
          <p className="truncate px-2 text-xs text-slate-400">{user?.email}</p>
          <button data-testid="sidebar-logout-btn" onClick={() => { logout(); nav("/login"); }} className="mt-2 flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-300 hover:bg-slate-800">
            <LogOut className="h-4 w-4" />Sign out
          </button>
        </div>
      </aside>
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white/90 px-4 backdrop-blur-md md:ml-64 md:px-8">
        <img src="/logo.png" alt="RingUs" data-testid="header-logo" className="h-8 w-auto md:hidden" />
        <span className="hidden md:block" />
        <SyncBadge />
      </header>
      <main className="px-4 pb-28 pt-5 md:ml-64 md:px-8 md:pb-10 md:pt-8">
        <div className="mx-auto max-w-6xl"><Outlet /></div>
        <SiteFooter />
      </main>
      <nav className="fixed bottom-0 left-0 right-0 z-50 flex h-16 items-center justify-around border-t border-slate-200 bg-white/95 px-1 backdrop-blur-md md:hidden">
        {[...MAIN.slice(0, 2), MAIN[2], MAIN[3], ["/more", "More", MoreHorizontal]].map(([to, l, I]) => (
          <NavLink key={to} to={to} end={to === "/"} data-testid={`bottomnav-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => `flex min-w-[60px] flex-col items-center gap-0.5 rounded-xl px-2 py-1.5 text-[11px] font-semibold ${to === "/capture" ? "" : isActive ? "text-teal-700" : "text-slate-500"}`}>
            {to === "/capture" ? (
              <span className="-mt-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-600 text-white shadow-lg shadow-teal-600/30"><I className="h-6 w-6" /></span>
            ) : <I className="h-5 w-5" />}
            <span>{l}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
